import { useState } from 'react'
import { useAiUsage, labelForFeature } from '../hooks/useAiUsage.js'
import { formatMoney } from '../lib/format.js'

// AI spend panel for SettingsScreen.
//
// FOUNDER / OPERATOR ONLY. Mounted from SettingsScreen behind an
// `isOperator` gate so paying customers never see the dollar value
// of token spend. Per-feature spend visibility is a back-office tool
// for trial-abuse detection and unit-economics monitoring — not part
// of the value proposition customers are paying for.
//
// Reads `ai_usage_for_restaurant(p_restaurant_id, p_since)` via the
// `useAiUsage` hook. Errors out quietly: if the RPC call fails (e.g.
// the migration hasn't been applied yet on a fresh project), the
// card just hides itself rather than blocking the rest of Settings.
export default function AiUsageCard() {
  const [windowDays, setWindowDays] = useState(30)
  const { data, loading, error } = useAiUsage({ days: windowDays })

  // Hide on error rather than show a scary panel — Settings is
  // already busy and this is informational.
  if (error) return null

  if (loading) {
    return (
      <section className="mx-4 mt-6 rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
        <div className="text-sm text-black/55">Loading AI usage…</div>
      </section>
    )
  }

  const totalCalls   = Number(data?.total_calls || 0)
  const totalCents   = Number(data?.total_cost_cents || 0)
  const byFeature    = Array.isArray(data?.by_feature) ? data.by_feature : []
  const totalDollars = totalCents / 100

  // Sort features by spend desc so the biggest cost driver is on top.
  const sortedFeatures = [...byFeature].sort(
    (a, b) => Number(b.cost_cents || 0) - Number(a.cost_cents || 0)
  )

  return (
    <section className="mx-4 mt-6 rounded-2xl border border-black/10 bg-white p-5 shadow-sm" aria-label="AI usage">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-black/55">
            AI usage
          </div>
          <div className="mt-1 text-xl font-semibold">
            {formatMoney(totalDollars)} <span className="text-sm font-normal text-black/55">spent</span>
          </div>
          <div className="text-xs text-black/55">
            {totalCalls.toLocaleString()} call{totalCalls === 1 ? '' : 's'} · last {windowDays} days
          </div>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setWindowDays(d)}
              className={`rounded-md border px-2 py-1 text-xs ${
                windowDays === d
                  ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold'
                  : 'border-black/15 text-black/65 hover:bg-black/5'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {totalCalls === 0 ? (
        <div className="mt-3 rounded-xl border border-dashed border-black/15 px-4 py-4 text-center text-sm text-black/55">
          No AI calls in the last {windowDays} days.
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-black/5 overflow-hidden rounded-xl border border-black/10">
          {sortedFeatures.map((row) => {
            const calls = Number(row.calls || 0)
            const cents = Number(row.cost_cents || 0)
            const share = totalCents > 0 ? (cents / totalCents) * 100 : 0
            return (
              <li key={row.feature} className="px-4 py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{labelForFeature(row.feature)}</div>
                    <div className="text-xs text-black/55">
                      {calls.toLocaleString()} call{calls === 1 ? '' : 's'}
                      {totalCents > 0 && ` · ${share.toFixed(0)}% of spend`}
                    </div>
                  </div>
                  <div className="text-sm font-semibold tabular-nums">
                    {formatMoney(cents / 100)}
                  </div>
                </div>
                {/* Tiny share bar so the eye picks the biggest line at a glance. */}
                {totalCents > 0 && (
                  <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-black/5">
                    <div
                      className="h-full bg-blue-500"
                      style={{ width: `${Math.max(2, share)}%` }}
                    />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <div className="mt-3 text-xs text-black/55">
        Updated in real time. Successful AI calls only — failed calls don't count.
      </div>
    </section>
  )
}
